import { useState, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, Transition } from '@headlessui/react'
import {
  ArrowUpIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  UserIcon,
  BanknotesIcon,
  PlusIcon,
  XMarkIcon,
  FunnelIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline'
import { walletApi, TransactionCategory, PaymentMethod } from '@/api/walletApi'
import { apiClient } from '@/api/client'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

const creditSchema = z.object({
  userId: z.string().min(1, 'Please select a user'),
  amount: z.number().min(1, 'Amount must be at least 1'),
  category: z.string().optional(),
  paymentMethod: z.string().optional(),
  externalReference: z.string().optional(),
  description: z.string().optional(),
  notes: z.string().optional(),
})

type CreditFormData = z.infer<typeof creditSchema>

const categories: { value: TransactionCategory; label: string }[] = [
  { value: 'TOP_UP', label: 'Top Up' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'DISCOUNT', label: 'Discount' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'OTHER', label: 'Other' },
]

const paymentMethods: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'MPESA', label: 'M-Pesa' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CARD', label: 'Card' },
  { value: 'SYSTEM', label: 'System' },
]

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function CreditWalletPage() {
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [lastTransaction, setLastTransaction] = useState<any>(null)
  const [filterCategory, setFilterCategory] = useState<string>('')
  const [filterPaymentMethod, setFilterPaymentMethod] = useState<string>('')

  const { register, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm<CreditFormData>({
    resolver: zodResolver(creditSchema),
    defaultValues: {
      category: 'TOP_UP',
      paymentMethod: 'CASH',
    },
  })

  const amount = watch('amount')

  // Fetch credit transactions (type: CREDIT)
  const { data: creditsData, isLoading: creditsLoading, refetch } = useQuery({
    queryKey: ['creditTransactions', filterCategory, filterPaymentMethod],
    queryFn: async () => {
      const params: Record<string, string> = { type: 'CREDIT', limit: '50' }
      if (filterCategory) params.category = filterCategory
      if (filterPaymentMethod) params.paymentMethod = filterPaymentMethod
      const response = await apiClient.get('/wallet/transactions', { params })
      return response.data
    },
  })

  // Search users for modal
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['searchUsers', searchQuery],
    queryFn: async () => {
      if (searchQuery.length < 2) return { data: { users: [] } }
      const response = await apiClient.get('/users', {
        params: { search: searchQuery, limit: 10 },
      })
      return response.data
    },
    enabled: searchQuery.length >= 2,
  })

  // Get selected user's wallet
  const { data: walletData } = useQuery({
    queryKey: ['userWallet', selectedUser?._id],
    queryFn: () => walletApi.getUserWallet(selectedUser._id),
    enabled: !!selectedUser?._id,
  })

  const creditMutation = useMutation({
    mutationFn: walletApi.creditWallet,
    onSuccess: (data) => {
      toast.success('Wallet credited successfully!')
      setLastTransaction(data.data)
      setShowSuccess(true)
      queryClient.invalidateQueries({ queryKey: ['userWallet'] })
      queryClient.invalidateQueries({ queryKey: ['creditTransactions'] })
      queryClient.invalidateQueries({ queryKey: ['allTransactions'] })
      queryClient.invalidateQueries({ queryKey: ['allWallets'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to credit wallet')
    },
  })

  const handleSelectUser = (user: any) => {
    setSelectedUser(user)
    setValue('userId', user._id)
    setSearchQuery('')
  }

  const onSubmit = (data: CreditFormData) => {
    if (!data.userId || !data.amount) {
      toast.error('User and amount are required')
      return
    }
    creditMutation.mutate({
      userId: data.userId,
      amount: data.amount,
      category: data.category as TransactionCategory,
      paymentMethod: data.paymentMethod as PaymentMethod,
      description: data.description,
      externalReference: data.externalReference,
      notes: data.notes,
    })
  }

  const handleOpenModal = () => {
    setShowSuccess(false)
    setSelectedUser(null)
    setLastTransaction(null)
    reset()
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setShowSuccess(false)
    setSelectedUser(null)
    setLastTransaction(null)
    reset()
  }

  const handleNewTransaction = () => {
    setShowSuccess(false)
    setSelectedUser(null)
    setLastTransaction(null)
    setSearchQuery('')
    reset({
      userId: '',
      amount: undefined,
      category: 'TOP_UP',
      paymentMethod: 'CASH',
      externalReference: '',
      description: '',
      notes: '',
    })
  }

  const credits = creditsData?.data?.transactions || []

  // Calculate totals
  const totalCredits = credits.reduce((sum: number, t: any) => sum + t.amount, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Credit Transactions</h1>
          <p className="text-gray-600">View all wallet credits and add new ones</p>
        </div>
        <button
          onClick={handleOpenModal}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
        >
          <PlusIcon className="w-5 h-5" />
          Credit Wallet
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Credits</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalCredits)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <ArrowUpIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{credits.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Credit</p>
              <p className="text-2xl font-bold text-gray-900">
                {credits.length > 0 ? formatCurrency(totalCredits / credits.length) : 'KES 0'}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <select
            value={filterPaymentMethod}
            onChange={(e) => setFilterPaymentMethod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Payment Methods</option>
            {paymentMethods.map((method) => (
              <option key={method.value} value={method.value}>{method.label}</option>
            ))}
          </select>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Credits List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {creditsLoading ? (
          <div className="p-8 text-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : credits.length === 0 ? (
          <div className="p-8 text-center">
            <BanknotesIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No credit transactions found</p>
            <button
              onClick={handleOpenModal}
              className="mt-4 px-4 py-2 text-green-600 hover:text-green-700 font-medium"
            >
              Add your first credit
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {credits.map((transaction: any) => (
                  <tr key={transaction._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <UserIcon className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {transaction.userId?.firstName} {transaction.userId?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{transaction.userId?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-green-600 font-semibold">
                        +{formatCurrency(transaction.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {transaction.category?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {transaction.paymentMethod?.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-xs font-mono text-gray-500">{transaction.reference}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(transaction.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Credit Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleCloseModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                  {showSuccess && lastTransaction ? (
                    <div className="p-6 text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircleIcon className="w-10 h-10 text-green-600" />
                      </div>
                      <Dialog.Title className="text-2xl font-bold text-gray-900 mb-2">
                        Credit Successful!
                      </Dialog.Title>
                      <p className="text-gray-600 mb-6">The wallet has been credited successfully.</p>

                      <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Reference</span>
                          <span className="font-mono text-sm">{lastTransaction.transaction.reference}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Amount Credited</span>
                          <span className="font-semibold text-green-600">
                            +{formatCurrency(lastTransaction.transaction.amount)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">New Balance</span>
                          <span className="font-semibold">{formatCurrency(lastTransaction.wallet.balance)}</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={handleNewTransaction}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          New Credit
                        </button>
                        <button
                          onClick={handleCloseModal}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                          Credit Wallet
                        </Dialog.Title>
                        <button
                          onClick={handleCloseModal}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <XMarkIcon className="w-6 h-6" />
                        </button>
                      </div>

                      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
                        {/* User Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Select User *</label>
                          {selectedUser ? (
                            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <UserIcon className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {selectedUser.firstName} {selectedUser.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">{selectedUser.email}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                {walletData?.data && (
                                  <p className="text-xs text-gray-500">
                                    Balance: <span className="font-semibold">{formatCurrency(walletData.data.balance)}</span>
                                  </p>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedUser(null)
                                    setValue('userId', '')
                                  }}
                                  className="text-xs text-red-600 hover:text-red-700"
                                >
                                  Change
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="relative">
                              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search by name, email, or phone..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />

                              {searchQuery.length >= 2 && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                                  {searchLoading ? (
                                    <div className="p-3 text-center">
                                      <LoadingSpinner size="sm" />
                                    </div>
                                  ) : searchResults?.data?.users?.length > 0 ? (
                                    searchResults.data.users.map((user: any) => (
                                      <button
                                        key={user._id}
                                        type="button"
                                        onClick={() => handleSelectUser(user)}
                                        className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 text-left"
                                      >
                                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                          <UserIcon className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="font-medium text-gray-900 text-sm truncate">
                                            {user.firstName} {user.lastName}
                                          </p>
                                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        </div>
                                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                          {user.role}
                                        </span>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="p-3 text-center text-sm text-gray-500">No users found</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          {errors.userId && (
                            <p className="mt-1 text-sm text-red-600">{errors.userId.message}</p>
                          )}
                        </div>

                        {/* Amount */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES) *</label>
                          <div className="relative">
                            <BanknotesIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="number"
                              {...register('amount', { valueAsNumber: true })}
                              placeholder="Enter amount"
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          {errors.amount && (
                            <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
                          )}
                        </div>

                        {/* Category and Payment Method */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                              {...register('category')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            >
                              {categories.map((cat) => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                            <select
                              {...register('paymentMethod')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            >
                              {paymentMethods.map((method) => (
                                <option key={method.value} value={method.value}>{method.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* External Reference */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">External Reference</label>
                          <input
                            type="text"
                            {...register('externalReference')}
                            placeholder="e.g., M-Pesa transaction ID"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>

                        {/* Description */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <input
                            type="text"
                            {...register('description')}
                            placeholder="e.g., Fee payment for Term 1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>

                        {/* Summary */}
                        {amount > 0 && selectedUser && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-700">Amount to Credit</span>
                              <span className="text-xl font-bold text-green-600">+{formatCurrency(amount)}</span>
                            </div>
                            {walletData?.data && (
                              <div className="flex items-center justify-between text-sm text-gray-600 mt-1">
                                <span>New Balance (Est.)</span>
                                <span className="font-semibold">{formatCurrency(walletData.data.balance + (amount || 0))}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Submit Button */}
                        <button
                          type="submit"
                          disabled={creditMutation.isPending || !selectedUser}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                        >
                          {creditMutation.isPending ? (
                            <>
                              <LoadingSpinner size="sm" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <ArrowUpIcon className="w-5 h-5" />
                              Credit Wallet
                            </>
                          )}
                        </button>
                      </form>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
